import { useMemo } from 'react';
import type { Product, OnboardingSystem } from '../pages/CatalogPage';
import type { Country, Level2PlatformData, Tab } from '../types';

type TabGenerationParams = {
  selectedPlatform: string | null;
  platformProducts: Product[];
  products: Product[];
  hardwareProducts: Product[];
  softwareProducts: Product[];
  unifiedVisibleCountries: Country[];
  level2Platforms: Record<string, Level2PlatformData>;
  lbm2Products: Product[];
  onboardingSystems: OnboardingSystem[];
  COUNTRIES: Country[];
  browseMode: boolean;
  selectedCountry: string | null;
}

/**
 * Custom hook for generating platform tabs
 * Extracted from ProductMatrixNew.tsx to reduce component complexity
 */
export const useTabGeneration = (params: TabGenerationParams): Tab[] => {
  const {
    selectedPlatform,
    platformProducts,
    products,
    hardwareProducts,
    softwareProducts,
    unifiedVisibleCountries,
    level2Platforms,
    onboardingSystems,
    COUNTRIES,
    browseMode,
    selectedCountry,
  } = params;

  return useMemo(() => {
    // Helper function to get filtered product count based on country availability
    const getFilteredProductCount = (products: Product[], visibleCountries: Country[]) => {
      return products.filter(product => {
        if (browseMode) {
          // Browse mode: check if product is available in any of the visible countries
          return visibleCountries.some(country => product.countries[country.code] === true);
        } else {
          // Location-filtered mode: check if product is available in the selected country
          return selectedCountry && product.countries[selectedCountry] === true;
        }
      }).length;
    };

    const tabs: Tab[] = [];

    // VAS platform: single Products tab
    if (selectedPlatform === 'VAS') {
      const vasProducts = platformProducts.filter(p => p.category !== 'Hardware');
      tabs.push({
        id: 'product-modules',
        label: 'Products',
        count: getFilteredProductCount(vasProducts, unifiedVisibleCountries),
      });
      return tabs;
    }

    // eCommerce platform: single Products tab
    if (selectedPlatform === 'eCommerce') {
      const ecomProducts = platformProducts.filter(p => p.category !== 'Hardware' && p.platform === 'eCommerce');
      tabs.push({
        id: 'product-modules',
        label: 'Products',
        count: getFilteredProductCount(ecomProducts, unifiedVisibleCountries),
      });
      return tabs;
    }

    // Payment Methods platform: Card Payments (only if non-empty) + Other Payment Methods
    if (selectedPlatform === 'Payment Methods') {
      const cardCategories = ['Payment Card', 'Wallets'];
      const cardProducts = platformProducts.filter(p => cardCategories.includes(p.category));
      const otherProducts = platformProducts.filter(p => !cardCategories.includes(p.category));
      if (cardProducts.length > 0) {
        tabs.push({
          id: 'card-payments',
          label: 'Card Payments',
          count: cardProducts.length,
          description: 'Card networks and digital wallets',
        });
      }
      tabs.push({
        id: 'other-payment-methods',
        label: 'Other Payment Methods',
        count: getFilteredProductCount(otherProducts, COUNTRIES),
        description: 'Local payment methods and payout solutions',
      });
      return tabs;
    }

    // Partner Portal platform: single Product Modules tab
    if (selectedPlatform === 'Partner Portal') {
      const ppProducts = platformProducts.filter(p => p.category !== 'Hardware');
      tabs.push({
        id: 'product-modules',
        label: 'Product Modules',
        count: getFilteredProductCount(ppProducts, unifiedVisibleCountries),
      });
      return tabs;
    }

    // Special case for Integrated Payments platform: Use vertical-based tabs, only for ISV Partner Integration category
    if (selectedPlatform === 'Integrated Payments') {
      // Group platform products by vertical, filtering only ISV Partner Integration products
      const verticalGroups: Record<string, Product[]> = {};
      platformProducts
        .filter(product => product.category === 'ISV Partner Integration')
        .forEach(product => {
          const vertical = product.vertical;
          // Only include products with valid verticals
          if (!vertical || vertical.trim() === '') {
            return; // Skip products without verticals
          }

          // Normalize vertical names for cleaner display
          let normalizedVertical = vertical;
          if (vertical === 'F&B') normalizedVertical = 'Food & Beverage';
          if (vertical === 'Auto Rental') normalizedVertical = 'Auto';
          if (vertical.startsWith('Ecommerce')) normalizedVertical = 'Ecommerce';
          if (vertical === 'Moto') normalizedVertical = 'MOTO';
          if (vertical.includes('Vending')) normalizedVertical = vertical.split('(')[0].trim(); // "Retail (Vending)" -> "Retail"

          if (!verticalGroups[normalizedVertical]) {
            verticalGroups[normalizedVertical] = [];
          }
          verticalGroups[normalizedVertical].push(product);
        });

      // Add "All" tab first showing all products
      const allProducts = platformProducts.filter(
        product => product.category === 'ISV Partner Integration'
      );
      tabs.push({
        id: 'vertical-all',
        label: 'All',
        count: allProducts.length,
        description: 'All partner integration solutions',
      });

      // Add "Pay at the Table" tab showing only PATT products
      const pattProducts = platformProducts.filter(
        product =>
          product.category === 'ISV Partner Integration' &&
          product.patt &&
          product.patt.trim() !== ''
      );
      tabs.push({
        id: 'vertical-patt',
        label: 'Pay at the Table',
        count: pattProducts.length,
        description: 'Partners with Pay at the Table support (Shift4 Mobile)',
      });

      // Create tabs for each vertical, sorted by custom order
      const verticalOrder = [
        'Food & Beverage',
        'Lodging',
        'Retail',
        'Auto',
        'MOTO',
        'Ecommerce',
        'Gaming',
        'Misc',
      ];
      Object.entries(verticalGroups)
        .sort(([a], [b]) => {
          const indexA = verticalOrder.indexOf(a);
          const indexB = verticalOrder.indexOf(b);
          // If not in custom order, put at end
          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        })
        .forEach(([vertical, products]) => {
          tabs.push({
            id: `vertical-${vertical.toLowerCase().replace(/\s+/g, '-').replace('&', 'and')}`,
            label: vertical,
            count: products.length,
            description: `${vertical} solutions and integrations`,
          });
        });
    } else {
      // Standard tab logic for other platforms

      // Tab order: Product Modules → Devices → Payments → Integrations

      // 1. Special case: Supported Partners tab for Shift4 Duo (appears first, loads by default)
      if (selectedPlatform === 'Shift4 Duo') {
        // Filter all products where patt has a value (PATT, OATT, or PATT / OATT)
        const supportedPartners = products.filter(p => p.patt && p.patt.trim() !== '');
        tabs.push({
          id: 'supported-partners',
          label: 'Supported Partners',
          count: getFilteredProductCount(supportedPartners, unifiedVisibleCountries),
          description: 'Partners with Pay at the Table support',
        });
      }

      // 2. Product Modules tab (formerly Solutions) - exclude for Shift4 Payments and Shift4 Duo
      if (selectedPlatform !== 'Shift4 Payments' && selectedPlatform !== 'Shift4 Duo') {
        tabs.push({
          id: 'product-modules',
          label: 'Product Modules',
          count: getFilteredProductCount(softwareProducts, unifiedVisibleCountries),
        });
      }

      // 3. Devices tab (exclude for Shift4 Payments and Shift4 Customer Hub)
      if (selectedPlatform !== 'Shift4 Payments' && selectedPlatform !== 'Shift4 Customer Hub') {
        tabs.push({
          id: 'supported-hardware',
          label: 'Supported Hardware',
          count: getFilteredProductCount(hardwareProducts, unifiedVisibleCountries),
        });
      }

      // 3. Special case: Add "Supported Payments" tab for Shift4 Dine, Shift4 Venue, Shift4 Solo, Shift4 Duo, Shift4 One
      if (
        selectedPlatform === 'Shift4 Dine' ||
        selectedPlatform === 'Shift4 Venue' ||
        selectedPlatform === 'Shift4 One' ||
        selectedPlatform === 'Shift4 Duo'
      ) {
        const paymentsData = level2Platforms['Shift4 Payments'];
        const supportedPayments = paymentsData
          ? paymentsData.products.filter(p => p.category !== 'Local Payment Methods')
          : [];
        tabs.push({
          id: 'supported-payments',
          label: 'Supported Payments',
          count: getFilteredProductCount(supportedPayments, COUNTRIES),
          description: 'Payment methods supported by this platform',
        });
      }

      // 4. Payment Types tab (from Level 2 Payments platform) - HIDDEN, replaced by Supported Payments tab for Shift4 Dine, Shift4 Solo, and Shift4 Duo
      // Only show Payment Methods tab for platforms that don't have Supported Payments tab and not Shift4 Customer Hub
      if (
        selectedPlatform !== 'Shift4 Dine' &&
        selectedPlatform !== 'Shift4 Venue' &&
        selectedPlatform !== 'Shift4 One' &&
        selectedPlatform !== 'Shift4 Duo' &&
        selectedPlatform !== 'Shift4 Customer Hub'
      ) {
        if (level2Platforms['Shift4 Payments']) {
          tabs.push({
            id: 'level2-payments',
            label: 'Payment Methods',
            count: getFilteredProductCount(
              level2Platforms['Shift4 Payments'].products,
              COUNTRIES
            ),
            description: level2Platforms['Shift4 Payments'].platform_description,
          });
        }
      }

      // 5. Integrations (Other Level 2 Platform tabs, excluding Payments and Integrated Payments for Shift4 Payments)
      Object.entries(level2Platforms).forEach(([platformName, platformData]) => {
        if (platformName !== 'Shift4 Payments') {
          // eCommerce is a standalone platform, never a sub-tab on other platforms
          if (platformName === 'eCommerce') return;

          // Skip Payments since it's already added as Payment Types
          // Exclude Marketplace Integrations for Shift4 Payments
          if (
            selectedPlatform === 'Shift4 Payments' &&
            platformName === 'Marketplace Integrations'
          ) {
            return; // Skip this tab
          }
          // Exclude Marketplace Integrations and Payment Methods for Shift4 Solo, Shift4 Duo, and Shift4 Venue
          if (
            (selectedPlatform === 'Shift4 One' ||
              selectedPlatform === 'Shift4 Duo' ||
              selectedPlatform === 'Shift4 Venue') &&
            (platformName === 'Marketplace Integrations' ||
              platformName === 'Shift4 Payments')
          ) {
            return; // Skip these tabs
          }
          // Exclude Marketplace Integrations for Shift4 Customer Hub
          if (
            selectedPlatform === 'Shift4 Customer Hub' &&
            platformName === 'Marketplace Integrations'
          ) {
            return; // Skip this tab
          }
          const filteredCount = getFilteredProductCount(
            platformData.products,
            unifiedVisibleCountries
          );
          tabs.push({
            id: `level2-${platformName.toLowerCase().replace(/\s+/g, '-')}`,
            label: platformName === 'Marketplace Integrations' ? 'Marketplace' : platformName,
            count: filteredCount,
            description: platformData.platform_description,
          });
        }
      });

      // 6. Special case: Add LBM 2.0 tab to SkyTab platform - TEMPORARILY HIDDEN
      // if (selectedPlatform === 'SkyTab' && lbm2Products.length > 0) {
      //   tabs.push({
      //     id: 'level3-lbm-2.0',
      //     label: 'LBM 2.0',
      //     count: getFilteredProductCount(lbm2Products, unifiedVisibleCountries),
      //     icon: Building2,
      //     description: 'Advanced version of Lighthouse Business Manager with enhanced tools for restaurants.'
      //   });
      // }
    }

    // 7. Add Onboarding tab as LAST tab for specific platforms
    const platformsWithOnboarding = [
      'Shift4 Dine',
      'Shift4 Venue',
      'Integrated Payments',
      'Shift4 Duo',
      'Shift4 One',
    ];
    if (
      selectedPlatform &&
      platformsWithOnboarding.includes(selectedPlatform) &&
      onboardingSystems.length > 0
    ) {
      // Count unique countries in onboarding data
      const uniqueCountries = new Set(onboardingSystems.map(system => system.country));
      tabs.push({
        id: 'onboarding',
        label: 'Onboarding',
        count: uniqueCountries.size,
        description: 'Onboarding and operational systems by country',
      });
    }

    // Filter pattern: suppress any tab whose available product count is zero.
    // Applies to all standard-platform tabs (Supported Partners, Product Modules,
    // Supported Hardware, level2 tabs, Onboarding, etc.).
    // Early-return platforms (VAS, eCommerce, Payment Methods, Partner Portal)
    // bypass this because they return before reaching this line.
    //
    // 'supported-payments' is exempt: it always shows for Dine/Venue/One/Duo.
    // The table components (DinePaymentsTable / Level2PlatformTableSection) handle
    // their own empty state via the Null Object pattern.
    return tabs.filter(tab => tab.id === 'supported-payments' || tab.count > 0);
  }, [
    selectedPlatform,
    platformProducts,
    products,
    hardwareProducts,
    softwareProducts,
    unifiedVisibleCountries,
    level2Platforms,
    onboardingSystems,
    COUNTRIES,
    browseMode,
    selectedCountry,
  ]);
};
